import {
  BLOCKED_TYPE_TEXT,
  BlockedCourseChipGroup,
  BlockedExamPasserFields,
  calculateBlockedDuration,
} from '@exam-center/blocked-user-management';
import { FormLeavingListener } from '@exam-center/components';
import {
  BlockedRecordBlockedScope,
  GetBlockedRecordByBlockedUserIdDocument,
  GetBlockedUserByIdDocument,
  useUpdateBlockedRecordMutation,
} from '@exam-center/graphql';
import { Flex, Form, FormButton, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, formatDate } from '@shared/utils';
import { useEffect, useMemo, useState } from 'react';

import {
  BLOCKED_RECORD_EXAM_PASSER_FORM_FIELD_NAME,
  type BlockedRecordExamPasserFormType,
  useBlockedRecordDetails,
  useBlockedRecordExamPasserForm,
} from '../../providers';
import { BlockedRecordRevocationButton } from '../blocked-record-revocation-button';

export default function BlockedRecordExamPasserFormLogic() {
  const { showPromiseSnackbar } = useSnackbar();
  const { blockedUser, blockedRecord } = useBlockedRecordDetails();
  const formContext = useBlockedRecordExamPasserForm();

  const [blockedDuration, setBlockedDuration] = useState<string>(
    calculateBlockedDuration(blockedRecord.blockedAt, blockedRecord.blockedUntil ?? new Date()),
  );
  const [fieldVisibilityStatus, setFieldVisibilityStatus] = useState<{
    enforcementEndDuration: boolean;
  }>({
    enforcementEndDuration: !!blockedRecord.blockedUntil,
  });
  const blockedCourses = blockedRecord.blockedRecordCourses.map((course) => ({
    code: course.examCourse?.code ?? '',
    name: course.examCourse?.name ?? '',
  }));
  const { examEvent } = blockedRecord;

  const isBlockedAllScope = blockedRecord.blockedScope === BlockedRecordBlockedScope.AllScope;

  const examEventSessionInfo = useMemo(() => {
    if (!examEvent?.sessions.length) return null;
    const examDate = formatDate(examEvent.sessions.at(0)?.startedAt);
    const examStartedAt = dayjs(examEvent.sessions.at(0)?.startedAt);
    const examEndedAt = dayjs(examEvent.sessions.at(-1)?.endedAt);
    return {
      examDate,
      startedAt: examStartedAt.format('HH:mm'),
      endedAt: examEndedAt.format('HH:mm'),
      round: examEvent.sessions.at(0)?.round.toString(),
    };
  }, [examEvent?.sessions]);

  const examCenterName = useMemo(() => {
    return blockedRecord.examCenter?.code && blockedRecord.examCenter.nameTH
      ? `${blockedRecord.examCenter?.code}: ${blockedRecord.examCenter.nameTH}`
      : '-';
  }, [blockedRecord.examCenter]);

  const [updateBlockedRecord, { loading: isUpdating }] = useUpdateBlockedRecordMutation({
    refetchQueries: [
      GetBlockedRecordByBlockedUserIdDocument,
      {
        query: GetBlockedUserByIdDocument,
        variables: {
          input: {
            id: blockedUser.id,
          },
        },
      },
    ],
  });

  const handleUpdateBlockedRecord = async (data: BlockedRecordExamPasserFormType) => {
    const isEnforcementEndAssign = data.enforcementEnd?.assign === 'true';
    if (!isEnforcementEndAssign) {
      formContext.setValue('enforcementEnd.date', undefined);
    }

    const blockedUntil = isEnforcementEndAssign ? data.enforcementEnd?.date : null;

    await updateBlockedRecord({
      variables: {
        input: {
          blockedRecordId: blockedRecord.id,
          info: {
            blockedReason: data.remark,
            blockedUntil,
          },
        },
      },
    });
  };

  const handleSubmit = async (data: BlockedRecordExamPasserFormType) => {
    await showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึกข้อมูล',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'บันทึกข้อมูลไม่สำเร็จ',
      },
      promise: () => handleUpdateBlockedRecord(data),
    });
  };

  const handleCompletedRevocationModal = () => {
    formContext.reset();
  };

  useEffect(() => {
    // REMARK: handle clear value
    const subscription = formContext.watch((value, { name }) => {
      // REMARK: handle clear value when enforcementEnd assign changes
      if (name === 'enforcementEnd.assign') {
        if (value.enforcementEnd?.assign === 'false') {
          formContext.resetField('enforcementEnd.date');
        }
      }

      // REMARK: handle calculate blockedUntil when enforcementEnd date changes
      if (name === 'enforcementEnd.date') {
        if (value.enforcementEnd?.date) {
          setBlockedDuration(
            calculateBlockedDuration(blockedRecord.blockedAt, value.enforcementEnd.date),
          );
        } else {
          setBlockedDuration('-');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext, blockedRecord.blockedAt]);

  useEffect(() => {
    const subscription = formContext.watch((value, { name }) => {
      if (name === 'enforcementEnd.assign') {
        setFieldVisibilityStatus((prev) => ({
          ...prev,
          enforcementEndDuration: value?.enforcementEnd?.assign === 'true',
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext.formState.isSubmitSuccessful, formContext]);

  return (
    <>
      <Form
        onSubmit={formContext.handleSubmit(handleSubmit)}
        sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(6) })}
      >
        <BlockedExamPasserFields
          fields={{
            blockedType: {
              mode: 'view',
              value: BLOCKED_TYPE_TEXT[blockedRecord?.blockedType],
            },
            blockedCourseType: {
              mode: 'view',
              value: isBlockedAllScope ? (
                <Text>ระงับสิทธิ์ทุกหลักสูตร</Text>
              ) : (
                <BlockedCourseChipGroup examCourseInfos={blockedCourses} />
              ),
              slotProps: {
                icon: { sx: { display: 'none' } },
              },
            },
            startedAt: {
              mode: 'view',
              value: formatDate(blockedRecord.blockedAt),
            },
            endedAt: {
              mode: 'input',
              assign: {
                name: 'enforcementEnd.assign',
                hiddenHelperText: true,
              },
              date: {
                name: 'enforcementEnd.date',
                required: true,
                hidden: !fieldVisibilityStatus.enforcementEndDuration,
              },
            },
            remark: {
              mode: 'input',
              name: BLOCKED_RECORD_EXAM_PASSER_FORM_FIELD_NAME.remark,
            },
            examDate: {
              mode: 'view',
              value: examEventSessionInfo?.examDate ?? '-',
            },
            examRound: {
              mode: 'view',
              value: examEventSessionInfo?.round ?? '-',
            },
            examStartTime: {
              mode: 'view',
              value: examEventSessionInfo?.startedAt ?? '-',
            },
            examEndTime: {
              mode: 'view',
              value: examEventSessionInfo?.endedAt ?? '-',
            },
            examCenter: {
              mode: 'view',
              value: examCenterName,
            },
          }}
          options={{
            shouldDisplayEndedAtAsDate: true,
            shouldDisplayBlockedCourseSelection: false,
            shouldDisplayExamCenterSelection: true,
          }}
          slots={{
            endedAtDateEndAdornment: (
              <Text variant='body2' sx={(theme) => ({ alignSelf: 'end', pb: theme.spacing(3) })}>
                (รวมระยะเวลาระงับสิทธิ์ทั้งหมด: {blockedDuration})
              </Text>
            ),
            blockedAtLabelText: 'วันที่เริ่มระงับสิทธิ์',
            blockedUntilLabelText: 'วันที่สิ้นสุดระงับสิทธิ์',
            blockedUntilAssignedLabelText: 'กำหนดวันที่สิ้นสุดระงับสิทธิ์',
          }}
        />
        <Flex sx={{ flex: 1, justifyContent: 'space-between' }}>
          <BlockedRecordRevocationButton
            disabled={isUpdating}
            onCompleted={handleCompletedRevocationModal}
          />
          <FormButton disabled={isUpdating} label='บันทึก' />
        </Flex>
      </Form>
      <FormLeavingListener onSubmit={handleSubmit} />
    </>
  );
}
